import type { Person } from './db';

const LIVING_MAX_AGE = 120;

// Parse partial ISO dates: 'YYYY', 'YYYY-MM', 'YYYY-MM-DD'. Returns year or null.
export function parseYear(s?: string): number | null {
  if (!s) return null;
  const m = /^(\d{4})/.exec(s.trim());
  return m ? Number(m[1]) : null;
}

// Treat a person as living unless we have evidence otherwise.
// - explicit livingOverride wins
// - death year present → not living
// - birth year older than LIVING_MAX_AGE → treat as deceased
// - no dates → living (safe default for POPIA)
export function isLiving(p: Pick<Person, 'dob' | 'death' | 'living' | 'livingOverride'>): boolean {
  if (p.livingOverride) return p.living === true;
  if (p.death && parseYear(p.death)) return false;
  const birth = parseYear(p.dob);
  if (birth != null) {
    const age = new Date().getFullYear() - birth;
    if (age > LIVING_MAX_AGE) return false;
  }
  return true;
}

// Redact a person record for public exposure. Strips identifying detail for
// living people unless explicitly authorised. Used on every public surface
// (search, public corpus, exports, share links without live-edit).
export function redactForPublic<T extends Person>(p: T): T {
  if (!isLiving(p) || p.publishAuthorized) return p;
  return {
    ...p,
    name: 'Living person',
    label: 'Living',
    dob: undefined,
    death: undefined,
    birthPlace: undefined,
    deathPlace: undefined,
    notes: undefined,
    link: undefined,
    extra: undefined,
  } as T;
}

export function redactList<T extends Person>(people: T[]): T[] {
  return people.map(redactForPublic);
}
